import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { api, errText } from "@/lib/api";
import { fmtDate, fmtMoney, fmtNumber } from "@/lib/format";
import { LdBadge, Mono, PriorityBadge } from "@/components/Badges";
import StageTracker from "@/components/StageTracker";
import { fieldsOf, Field, payloadFrom } from "@/components/StepFields";
import { Button } from "@/components/ui/button";
import { useCustomer } from "@/lib/customer";
import { toast } from "sonner";

/** Record the current step without leaving the list. */
function InlineStep({ group, onSaved }) {
  const { isAllCustomers, stages, stagesReady } = useCustomer();
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const stage = group.lead.stage;
  const fields = fieldsOf(stages, stage);
  const lineIds = group.lines.map((l) => l.id);

  const save = async () => {
    const payload = payloadFrom(values);
    if (Object.keys(payload).length === 0) {
      toast.info("Fill in at least one box first");
      return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.patch(`/polines/${group.lead.lineId}`, { values: payload, lineIds });
      if ((res.refused || []).length) {
        toast.error(`Not saved: ${res.refused.join(", ")} — not part of this PO's flow any more.`);
        return;
      }
      toast.success(res.linesUpdated > 1 ? `Recorded on ${res.linesUpdated} lines` : "Recorded");
      setValues({});
      await onSaved();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-md border border-border bg-background p-3" data-testid={`inline-step-${group.key}`}>
      <div className="text-xs font-medium">{group.lead.stageLabel}</div>
      {isAllCustomers ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid={`inline-step-read-only-${group.key}`}>
          Open the PO, or pick this customer, to record anything here.
        </p>
      ) : !stagesReady ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid={`inline-step-loading-${group.key}`}>
          Fetching this customer's flow…
        </p>
      ) : fields.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid={`inline-step-empty-${group.key}`}>
          There is nothing to record at this stage.
        </p>
      ) : (
        <>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map((def) => (
              <Field
                key={def.key}
                name={def.key}
                def={def}
                value={values[def.key] ?? ""}
                onChange={(n, v) => setValues((s) => ({ ...s, [n]: v }))}
                prefix={`inline-${group.key}`}
              />
            ))}
          </div>
          <Button size="sm" className="mt-3" onClick={save} disabled={busy || isAllCustomers} data-testid={`inline-save-${group.key}`}>
            Record it
          </Button>
        </>
      )}
      {group.counts.total > 1 && stage === 6 && (
        <p className="mt-2 text-xs text-muted-foreground">
          This records the dispatch for the whole PO. Open the PO screen to mark it as dispatched in part.
        </p>
      )}
    </div>
  );
}

/** One collapsible row for a PO and branch, with its tracker, follow-ups and inline step. */
export default function PoGroupRow({ group, isOpen, toggle, openStage, setOpenStage, reload, tasks = [], testPrefix = "my-day" }) {
  const navigate = useNavigate();
  const c = group.counts;
  return (
    <div data-testid={`${testPrefix}-card-${group.key}`}>
      <button
        onClick={toggle}
        className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-secondary/60 sm:px-4"
        data-testid={`${testPrefix}-expand-${group.key}`}
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium sm:text-base">
            {group.branchName || "Branch not named"}
          </span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            <Mono>{group.poNumber || "PO not numbered"}</Mono> · <Mono>{c.total}</Mono> item
            {c.total === 1 ? "" : "s"}
            {tasks.length > 0 && (
              <>
                {" · "}
                <Mono>{tasks.length}</Mono> follow-up{tasks.length === 1 ? "" : "s"}
              </>
            )}
            {group.expectedMaterialReadyDate && (
              <>
                {" · material by "}
                <Mono>{fmtDate(group.expectedMaterialReadyDate)}</Mono>
              </>
            )}
          </span>
        </span>
        {tasks.length > 0 ? (
          <PriorityBadge priority={tasks[0].priority} testId={`${testPrefix}-priority-${group.key}`} />
        ) : (
          <LdBadge colour={group.ldColour} text={group.ldStatus} testId={`${testPrefix}-ld-${group.key}`} />
        )}
      </button>

      {isOpen && (
        <div className="border-t border-border bg-secondary/20 px-3 py-4 sm:px-4" data-testid={`${testPrefix}-detail-${group.key}`}>
          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            <div>
              <div className="text-muted-foreground">PO date</div>
              <Mono>{fmtDate(group.poDate)}</Mono>
            </div>
            <div>
              <div className="text-muted-foreground">Delivery due</div>
              <Mono>{fmtDate(group.deliveryDue)}</Mono>
            </div>
            <div>
              <div className="text-muted-foreground">Completion due</div>
              <Mono>{fmtDate(group.completionDue)}</Mono>
            </div>
            <div>
              <div className="text-muted-foreground">Ageing / value</div>
              <Mono>{group.ageing}d</Mono>
              <span className="text-muted-foreground"> · {fmtMoney(group.value)}</span>
            </div>
            {group.expectedMaterialReadyDate && (
              <div data-testid={`${testPrefix}-material-date-${group.key}`}>
                <div className="text-muted-foreground">Material wanted by</div>
                <Mono>{fmtDate(group.expectedMaterialReadyDate)}</Mono>
              </div>
            )}
          </div>

          {tasks.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid={`${testPrefix}-tasks-${group.key}`}>
              {tasks.map((task) => (
                <li key={task.id}>
                  <a
                    href={`/po-history/${task.lineId}?task=${encodeURIComponent(task.id)}${task.stage ? `&stage=${task.stage}` : ""}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-md border border-border bg-background p-2 text-left text-xs transition-colors hover:border-primary hover:bg-accent"
                    data-testid={`task-card-${task.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium">{task.title}</span>
                      <PriorityBadge priority={task.priority} testId={`task-priority-${task.id}`} />
                    </div>
                    <p className="mt-1 text-muted-foreground">{task.why}</p>
                    <div className="mt-1 flex items-center justify-between gap-2 text-muted-foreground">
                      <span>
                        {task.itemType || "—"} · due <Mono>{fmtDate(task.dueDate)}</Mono> · {task.owner}
                      </span>
                      <span className="flex shrink-0 items-center gap-1 text-primary">
                        Open and record <ExternalLink className="h-3 w-3" />
                      </span>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4">
            <StageTracker
              steps={group.steps || []}
              testId={`${testPrefix}-tracker-${group.key}`}
              onStageClick={(step) => setOpenStage(openStage === step.stage ? null : step.stage)}
            />
          </div>

          {openStage === group.lead.stage ? (
            <InlineStep group={group} onSaved={reload} />
          ) : (
            <div className="mt-2 rounded-md border border-border bg-background p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                What to record now
              </div>
              <p className="mt-1 text-sm" data-testid={`${testPrefix}-next-action-${group.key}`}>
                {group.lead.nextAction}
              </p>
            {group.counts.total > 1 && (
              <p className="mt-1 text-xs" data-testid={`${testPrefix}-progress-${group.key}`}>
                <span className="text-muted-foreground">
                  <Mono>{group.counts.total}</Mono> items on this PO
                  {(group.partialMarks || []).length > 0 && ` · ${group.partialMarks.join(", ")} in part`}
                </span>
              </p>
            )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => setOpenStage(group.lead.stage)}
                  data-testid={`${testPrefix}-record-here-${group.key}`}
                >
                  Record it here
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/po-history/${group.lead.lineId}`)}
                  data-testid={`${testPrefix}-open-${group.key}`}
                >
                  Open the PO screen
                </Button>
              </div>
            </div>
          )}

          {c.total > 1 && (
            <ul className="mt-3 divide-y divide-border border-t border-border text-xs" data-testid={`${testPrefix}-items-${group.key}`}>
              {group.lines.map((line) => (
                <li key={line.id} className="py-2" data-testid={`${testPrefix}-line-${line.id}`}>
                  <span className="font-medium">{line.itemType || "—"}</span>
                  {line.qty ? <Mono> × {fmtNumber(line.qty)}</Mono> : null}
                  {line.product && <span className="ml-1 text-muted-foreground">— {line.product}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
